import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Inquiry = {
  id: number;
  name: string;
  email: string;
  company: string;
  problem: string;
  timeline: string;
  createdAt: string;
};

type SessionState = "checking" | "signed-out" | "signed-in";

async function getInquiries(): Promise<Inquiry[]> {
  const response = await fetch("/api/inquiries", { credentials: "same-origin" });
  if (!response.ok) {
    throw new Error("Unable to load inquiries.");
  }
  return response.json() as Promise<Inquiry[]>;
}

export default function Admin() {
  const [sessionState, setSessionState] = useState<SessionState>("checking");
  const [password, setPassword] = useState("");
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingInquiryId, setDeletingInquiryId] = useState<number | null>(null);

  const loadInquiries = async () => {
    try {
      const nextInquiries = await getInquiries();
      setInquiries(nextInquiries);
      setError("");
    } catch {
      setSessionState("signed-out");
      setError("Your session has ended. Sign in again to review inquiries.");
    }
  };

  const deleteInquiry = async (inquiry: Inquiry) => {
    if (!window.confirm("Permanently delete this inquiry? This cannot be undone.")) {
      return;
    }

    setDeletingInquiryId(inquiry.id);
    setError("");
    try {
      const response = await fetch(`/api/inquiries/${inquiry.id}`, {
        method: "DELETE",
        credentials: "same-origin",
      });

      if (response.status === 401) {
        setSessionState("signed-out");
        setError("Your session has ended. Sign in again to review inquiries.");
        return;
      }

      if (!response.ok) {
        throw new Error("Unable to delete inquiry.");
      }

      setInquiries((current) =>
        current.filter((currentInquiry) => currentInquiry.id !== inquiry.id),
      );
    } catch {
      setError("Could not delete this inquiry. Refresh and try again.");
    } finally {
      setDeletingInquiryId(null);
    }
  };

  useEffect(() => {
    let active = true;

    fetch("/api/admin/session", { credentials: "same-origin" })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error("Session check failed");
        }
        return response.json() as Promise<{ authenticated: boolean }>;
      })
      .then(async ({ authenticated }) => {
        if (!active) return;
        if (!authenticated) {
          setSessionState("signed-out");
          return;
        }
        setSessionState("signed-in");
        await loadInquiries();
      })
      .catch(() => {
        if (active) {
          setSessionState("signed-out");
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const signIn = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      const response = await fetch("/api/admin/login", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        throw new Error("Invalid password");
      }

      setPassword("");
      setSessionState("signed-in");
      await loadInquiries();
    } catch {
      setError("The password was not accepted.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const signOut = async () => {
    await fetch("/api/admin/logout", {
      method: "POST",
      credentials: "same-origin",
    });
    setInquiries([]);
    setSessionState("signed-out");
    setError("");
  };

  if (sessionState === "checking") {
    return (
      <main className="min-h-[100svh] bg-background px-4 py-24 text-foreground sm:px-6 lg:px-8">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Checking access</p>
      </main>
    );
  }

  if (sessionState === "signed-out") {
    return (
      <main className="min-h-[100svh] bg-background px-4 py-16 text-foreground sm:px-6 lg:px-8">
        <div className="mx-auto max-w-md border border-card-border bg-card p-8 sm:p-10">
          <a href="/" className="font-display text-2xl font-bold tracking-tight text-primary">ATLAS AG</a>
          <div className="mt-16 border-l-4 border-accent pl-6">
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Admin access</p>
            <h1 className="mt-4 font-display text-5xl uppercase leading-[0.85] tracking-tighter text-primary">Review inquiries.</h1>
          </div>
          <form onSubmit={signIn} className="mt-12 space-y-6">
            <Input
              type="email"
              name="username"
              autoComplete="username"
              tabIndex={-1}
              aria-hidden="true"
              className="absolute -left-[10000px] h-px w-px overflow-hidden"
            />
            <div className="space-y-3">
              <Label htmlFor="admin-password" className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
              <Input
                id="admin-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                className="h-14 rounded-none border-border bg-background px-4 text-base focus-visible:ring-accent"
              />
            </div>
            {error && <p role="alert" className="font-mono text-xs uppercase tracking-widest text-destructive">{error}</p>}
            <Button type="submit" disabled={isSubmitting} className="h-14 w-full rounded-none bg-accent font-mono text-xs uppercase tracking-widest text-white hover:bg-accent/90">
              {isSubmitting ? "Checking…" : "Continue"}
            </Button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-[100svh] bg-background px-4 py-10 text-foreground sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1400px]">
        <header className="flex flex-col gap-8 border-b border-border pb-10 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <a href="/" className="font-display text-2xl font-bold tracking-tight text-primary">ATLAS AG</a>
            <h1 className="mt-12 font-display text-5xl uppercase leading-[0.85] tracking-tighter text-primary sm:text-7xl">Build inquiries</h1>
          </div>
          <Button type="button" onClick={signOut} variant="outline" className="h-11 rounded-none border-border bg-transparent font-mono text-xs uppercase tracking-widest">
            Sign out
          </Button>
        </header>

        <div className="mt-10 flex items-center justify-between gap-4">
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{inquiries.length} received</p>
          <Button type="button" onClick={loadInquiries} variant="outline" className="h-11 rounded-none border-border bg-transparent font-mono text-xs uppercase tracking-widest">
            Refresh
          </Button>
        </div>
        {error && <p role="alert" className="mt-6 font-mono text-xs uppercase tracking-widest text-destructive">{error}</p>}

        <div className="mt-8 grid gap-6">
          {inquiries.length === 0 ? (
            <div className="border border-card-border bg-card p-8 text-muted-foreground">No inquiries yet.</div>
          ) : (
            inquiries.map((inquiry) => (
              <article key={inquiry.id} className="border border-card-border bg-card p-6 sm:p-8">
                <div className="flex flex-col justify-between gap-4 border-b border-card-border pb-6 sm:flex-row">
                  <div>
                    <h2 className="font-display text-3xl uppercase tracking-tighter text-primary">{inquiry.name}</h2>
                    <a href={`mailto:${inquiry.email}`} className="mt-2 inline-flex font-mono text-sm text-muted-foreground underline decoration-accent underline-offset-4 hover:text-primary">{inquiry.email}</a>
                  </div>
                  <div className="flex flex-col items-start gap-4 sm:items-end">
                    <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground sm:text-right">
                      <div>{new Date(inquiry.createdAt).toLocaleString()}</div>
                      <div className="mt-2 text-accent">{inquiry.timeline}</div>
                    </div>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      disabled={deletingInquiryId !== null}
                      onClick={() => void deleteInquiry(inquiry)}
                    >
                      {deletingInquiryId === inquiry.id ? "Deleting…" : "Delete inquiry"}
                    </Button>
                  </div>
                </div>
                <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_2fr]">
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Company</p>
                    <p className="mt-2 text-primary">{inquiry.company}</p>
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Problem</p>
                    <p className="mt-2 whitespace-pre-wrap leading-relaxed text-primary">{inquiry.problem}</p>
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
      </div>
    </main>
  );
}